import { useNavigate } from 'react-router-dom';
import Table from '@/components/Table';
import agentMutations from '@/api/mutitations/agent';
import { notifications } from '@mantine/notifications';
import prepareTranslate from '@/helpers/dictionary';
import { routeWithParams } from '@/helpers/route';
import routes from '@/routes';
import { Button } from '@mantine/core';
import agentApi from '@/api/services/agent';
import {
  IconEdit,
  IconEye,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import docsMutation from '@/api/mutitations/docs';
import PageTemplate from '@/components/PageTemplate';
import { useState } from 'react';
import { extractFilterOptions } from '@/components/Table/Table.functions';

const Agents = () => {
  const t = prepareTranslate();
  const navigate = useNavigate();
  const [filterOptions, setFilterOptions] = useState({});

  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();

  // eslint-disable-next-line consistent-return
  const getData = async (start, end, query, sort) => {
    try {
      const getAgentsDataObj = {
        start,
        end,
        query,
        sort,
      };

      if (query.searchElements?.agreements) {
        // eslint-disable-next-line no-param-reassign
        query.searchElements.agreements = { ...query.searchElements.agreements, status: { $ne: 'archive' } };
      }
      if (query.searchElements?.clients) {
        // eslint-disable-next-line no-param-reassign
        query.searchElements.clients = { ...query.searchElements.clients, status: { $ne: 'archived' } };
      }
      if (query.searchElements?.alternativeFunds) {
        // eslint-disable-next-line no-param-reassign
        query.searchElements.alternativeFunds = { ...query.searchElements.alternativeFunds, status: { $ne: 'archived' } };
      }

      const agentsData = await agentMutate(getAgentsDataObj);
      const newFilterOptions = extractFilterOptions(agentsData.data, mockColumns);
      setFilterOptions(newFilterOptions);

      return agentsData;
    } catch (error) {
      notifications.show({
        title: t('pages.agents.notification.errors.find.title'),
        message: t('pages.agents.notification.errors.find.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };

  const handleClickOnView = (row) => {
    const id = row._id || row;
    navigate(routeWithParams(routes.agentView.path, { id, tab: 'details' }));
  };

  const handleCreateClick = () => {
    navigate(routes.agentCreate.path);
  };

  const handleArchiveClick = () => {
    navigate(routes.agentsArchived.path);
  };

  const handleClickOnEdit = (id) => {
    navigate(routeWithParams(routes.agentView.path, { id, tab: 'details', state: 'edit' }));
  };

  const handleClickOnDelete = async (items = []) => {
    try {
      await agentApi.delete({ data: items });

      // TODO reload page in better way
      // refreshRows(items, 'delete');
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    } catch {
      notifications.show({
        title: t('pages.agents.notification.errors.delete.title'),
        message: t('pages.agents.notification.errors.delete.message'),
      });
    }
  };

  // eslint-disable-next-line consistent-return
  const createFile = async (option, query) => {
    const requestColumns = mockColumns.filter((column) => column.title !== 'פעולות');
    const exportData = {
      query,
      fileType: option,
      service: 'agent',
      columns: requestColumns,
      titleArray: ['personalDetails.fullName'],
    };

    const fileTitleBase = t('pages.agents.fileTitle');
    const fileTitle = option === 'pdf' ? `${fileTitleBase}.pdf` : `${fileTitleBase}.xlsx`;

    try {
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.agents.notification.errors.export.title'),
        message: t('pages.agents.notification.errors.export.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };

  const mockColumns = [
    {
      title: 'תעודת זהות',
      accessor: 'personalDetails.uid',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
    },
    {
      title: 'שם פרטי',
      accessor: 'personalDetails.firstName',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
      transforms: [{
        func: 'button',
        params: {
          onClick: handleClickOnView,
          variant: 'outline',
        },
      }],
    },
    {
      title: 'שם משפחה',
      accessor: 'personalDetails.lastName',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
    },
    {
      title: 'טלפון',
      accessor: 'personalDetails.phone',
      isVisible: true,
      transforms: ['phone'],
      isFilterActive: true,
      sortable: true,
    },
    {
      title: 'דואר אלקטרוני',
      accessor: 'personalDetails.email',
      isVisible: true,
      transforms: ['email'],
      isFilterActive: true,
      sortable: true,
    },
    {
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          action: handleClickOnView,
          icon: <IconEye />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: 'עריכה',
          action: handleClickOnEdit,
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
        },
        // {
        //   title: 'הפוך לפעיל/לא פעיל',
        //   action: handleClickOnEdit,
        //   icon: <IconEdit />,
        //   showLabel: false,
        //   isPrimary: false,
        // },
      ],
    },
  ];

  const mockSelectionActions = [
    {
      title: 'מחיקה',
      icon: <IconX stroke='1' />,
      onClick: handleClickOnDelete,
    },
  ];

  const exportOptions = ['excel'];

  const initialColumnFilters = mockColumns
    .filter((column) => column.accessor !== 'actions')
    .reduce((acc, column) => {
      acc[column.accessor] = '';
      return acc;
    }, {});

  const [columnFilters, setColumnFilters] = useState(initialColumnFilters);

  const searchInputProps = {
    placeholder: t('pages.agents.searchInputPlaceholder'),
    searchKey: {
      agreements: ['agentNumber'],
      clients: ['clientNumber'],
      alternativeFunds: ['alternativeFundNumber'],
    },
  };

  return (
    <>
      <PageTemplate
        title={t('pages.agents.title')}
        actions={[
          <Button key='add agent' leftSection={<IconPlus />} onClick={handleCreateClick}>{t('pages.agents.action')}</Button>,
          <Button leftSection={<IconEye />} key='archive' variant='outline' onClick={handleArchiveClick}>{t('pages.agents.archive') }</Button>,
        ]}
      >

          <Table
            isExportActive
            columns={mockColumns}
            getData={getData}
            selectionActions={mockSelectionActions}
            isColumnsActive={false}
            exportOptions={exportOptions}
            createFile={createFile}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            filterOptions={filterOptions}
            withSearchInput={true}
            searchInputProps={searchInputProps}
          />
      </PageTemplate>
    </>
  );
};

export default Agents;
